import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useControllableValue, useMemoizedFn } from 'ahooks';
import { compact } from 'lodash';
import { Button, Space } from '@bedrock/components';
import { Upload, ChevronRight } from '@bedrock/icons-react';
import { useImportFileModal } from '@mpc/common/src/features/importFileModal';
import classNames from 'classnames/bind';
import styles from './index.less';
import { SFormElement } from '#components/SForm';
import DetailsTable from '#features/DetailsTable';
import {
  QuoteDetailShowDrawer,
  QuoteDetailShowDrawerRef,
} from '#features/QuoteDetailShow';
import { localPx2rem } from '#utils';
import SupplierQuoteButton from '#features/MPCDemand/SupplierQuoteButton';

const cx = classNames.bind(styles);

type Props = {
  isAll?: boolean;
  view?: boolean;
  detail?: any;
  formData?: any;
  quoteIndex?: number; // 报价下标
  value?: any;
  onChange?: (value: any) => void;
  supplierQuotationTableConfig?: any;
};

const QuoteFileDetail = (props: Props) => {
  const { view, detail, quoteIndex, isAll, formData, supplierQuotationTableConfig } = props;
  const [detailsTableVisible, setDetailsTableVisible] = useState(false);
  const [quoteTableConfig, setQuoteTableConfig] = useState({} as any);
  const [value, setValue] = useControllableValue(props, {
    defaultValue: [],
  });

  const quoteDetailShowDrawerRef = useRef<QuoteDetailShowDrawerRef>(null);
  const sVersion = detail?.structured_version ?? 0;

  useEffect(() => {
    if (props.value !== value) {
      setValue(props.value);
    }
  }, [props.value]);

  const mergeUploadId =
    detail?.depth === 2 ? detail?.demand_plan_info?.demand_id : detail?.id;

  const { openImportFileModal, closeImportFileModal } = useImportFileModal({
    uploadFinishedCallback(data) {
      if (data.code === 1 && data?.data?.fail_count === 0) {
        if (isAll) {
          setValue([
            {
              id: data?.data?.ext_data?.[0]?.blob_id,
              blob: {
                ...data?.data?.ext_data?.[0],
                id: data?.data?.ext_data?.[0]?.blob_id,
              },
              // 总报价明细上传后，生成各个合作内容的报价明细，需分配至各个合作内容
              allQuoteAttachData: data?.data?.ext_data,
            },
          ]);
        } else {
          setValue([
            {
              id: data?.data?.ext_data?.blob_id,
              blob: {
                ...data?.data?.ext_data,
                id: data?.data?.ext_data?.blob_id,
              },
              data: data?.data,
            },
          ]);
        }
        closeImportFileModal();
      } else {
        setValue([]);
      }
    },
    scene: isAll
      ? mergeUploadId
      : detail?.project_plan_info?.cooperate_accounts?.[quoteIndex]?.id,
  });

  const handleQuotationDetails = async () => {
    const val = value?.[0] || {};

    // 当本地上传时，id和blob_id相等，代表是本地上传的文件，还没提交
    // 调用需要传blob_id的接口
    const isBlob = val?.id === val?.blob?.blob_id;

    if (sVersion >= 1) {
      quoteDetailShowDrawerRef.current?.open?.({
        drawerProps: {
          title: compact([
            isAll ? '报价总明细' : '报价明细',
            supplierQuotationTableConfig?.supplier_full_name || detail?.demand_suppliers?.[0]?.supplier_full_name
          ]).join(' - '),
        },
        blobId: isBlob ? val?.blob?.blob_id : val?.blob?.id,
        canShowDiff: !["pending_quote"].includes(detail?.state?.toLowerCase()),
        cooperateAccounts: detail?.demand_plan_info?.cooperate_accounts,
        demandId: detail?.id,
        diffLabel:
          detail?.depth === 2 ? '显示结案报价与中标报价变更差异' : undefined,
        supplierId: supplierQuotationTableConfig?.id || detail?.demand_suppliers?.[0]?.id,
        cooperateAccountId:
          detail?.project_plan_info?.cooperate_accounts?.[quoteIndex]?.id,
      });
      return;
    }

    setQuoteTableConfig({
      id: val?.id,
      isAll,
      demand_supplier_quote_id:
        detail?.demand_suppliers?.[0]?.demand_supplier_quotes[quoteIndex]?.id,
      demand_supplier_id: detail?.demand_suppliers?.[0]?.id,
      isBlob,
      cooperate_accounts: detail?.demand_plan_info?.cooperate_accounts,
      isShowSettle: detail?.depth === 2, // 是否显示结算报价
      demand_supplier_quotes:
        detail?.demand_suppliers?.[0]?.demand_supplier_quotes,
    });
    setDetailsTableVisible(true);
  };

  return (
    <div>
      <Space direction="vertical" size={0} className={cx('upload-container')}>
        {!view ? (
          <div>
            <>
              {sVersion >= 1 && (
                <SupplierQuoteButton
                  detail={detail}
                  formData={formData}
                  onCommit={(res) => {
                    // 存入数据
                    const infos = res.data.cooperate_quote_infos;
                    if (infos?.[0]) {
                      setValue([
                        {
                          id: infos[0]?.blob_id,
                          blob: {
                            ...infos[0],
                            id: infos[0]?.blob_id,
                            byte_size: infos[0]?.size || infos[0]?.byte_size,
                          },
                          // 总报价明细上传后，生成各个合作内容的报价明细，需分配至各个合作内容
                          allQuoteAttachData: Object.keys(infos).reduce((prev, curr) => {
                            prev[curr] = {
                              ...infos[curr],
                              byte_size: infos[curr]?.size || infos[curr]?.byte_size,
                            };
                            return prev;
                          }, {}),
                        },
                      ]);
                    } else {
                      setValue([]);
                    }
                  }}
                />
              )}
              {sVersion < 1 && (
                <>
                  <Button
                    icon={<Upload />}
                    type="checked"
                    disabled={!!value?.length || view}
                    onClick={() => {
                      openImportFileModal({
                        limit: 999,
                        importType: isAll
                          ? `demand_supplier_quote_detail$merge_${mergeUploadId}`
                          : (`demand_supplier_quote_detail$new_${detail?.l3_category_id}` as any),
                      });
                    }}
                  >
                    上传文件
                  </Button>
                  <div className={cx('upload-tip')}>
                    仅支持1个，文件不能超过50M
                  </div>
                </>
              )}
            </>
          </div>
        ) : null}

        {!!value?.length && (
          <div className={cx('quotation-detail-file')}>
            <SFormElement
              view={view || sVersion >= 1}
              componentClass="upload"
              formItemKey={`1`}
              value={value}
              onChange={(value) => {
                setValue([]);
              }}
              style={{ maxWidth: localPx2rem(255) }}
            />
          </div>
        )}
        {!!value?.length && (
          <Button
            className={cx('view-quotation-details-btn')}
            type="link"
            onClick={handleQuotationDetails}
          >
            查看报价明细 <ChevronRight />
          </Button>
        )}
      </Space>
      <DetailsTable
        quoteIndex={quoteIndex}
        quoteTableConfig={quoteTableConfig}
        detailsTableVisible={detailsTableVisible}
        setDetailsTableVisible={setDetailsTableVisible}
      />
      <QuoteDetailShowDrawer ref={quoteDetailShowDrawerRef} />
    </div>
  );
};

export default QuoteFileDetail;
